import { Exchange } from '../proto/generated/common'
import { Segment } from '../proto/generated/security'
import {
    ExchangeTradingSession,
    TradingCalendarData,
    TradingSession,
    TradingSessionScope
} from '../proto/generated/trading-calendar'
import { toDayStart } from './dateTime'

/** Minimal security fields needed to resolve the correct trading session. */
export type TradingSessionSecurityRef = {
    exchange?: Exchange
    segment?: Segment
    partOfCAS?: boolean
}

/**
 * CAS scope applies only to CM securities that participate in closing auction.
 * Matches backend TradingCalendarCache.getTradingSessionsForSecurity.
 */
export function resolveTradingSessionScope(security: TradingSessionSecurityRef | undefined): TradingSessionScope {
    if (security?.segment === Segment.CM && security.partOfCAS === true) {
        return TradingSessionScope.CAS
    }
    return TradingSessionScope.DEFAULT
}

function hasSegment(segment: Segment | undefined): segment is Segment {
    return segment != null && segment !== Segment.SEGMENT_UNKNOWN && segment !== Segment.UNRECOGNIZED
}

/** Normalize to `yyyy-MM-dd` for comparing with applicable_from / applicable_to. */
export function toApplicableDateKey(date: Date | string): string {
    if (typeof date === 'string') {
        const trimmed = date.trim()
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
        // Holiday keys are often yyyyMMdd
        if (/^\d{8}$/.test(trimmed)) {
            return `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(6, 8)}`
        }
        return trimmed
    }
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
}

/**
 * Empty applicable_from = always; empty applicable_to = open-ended end (till present / ongoing).
 * Bounds are inclusive, matching backend TradingSession.isApplicableOn.
 */
export function isTradingSessionApplicableOn(session: TradingSession, date: Date | string): boolean {
    const day = toApplicableDateKey(date)
    const from = session.applicableFrom?.trim() || ''
    const to = session.applicableTo?.trim() || ''
    const fromOk = !from || day >= from
    const toOk = !to || day <= to
    return fromOk && toOk
}

/**
 * Among sessions applicable on `date`, prefer the most recently started version
 * (latest non-empty applicable_from). Matches backend resolveSession.
 */
export function pickApplicableSession(
    sessions: TradingSession[],
    date: Date | string
): TradingSession | undefined {
    const applicable = sessions.filter((s) => isTradingSessionApplicableOn(s, date))
    if (!applicable.length) return undefined
    if (applicable.length === 1) return applicable[0]
    return applicable.reduce((best, s) => {
        const a = s.applicableFrom?.trim() || ''
        const b = best.applicableFrom?.trim() || ''
        // Empty applicable_from sorts as earliest (LocalDate.MIN parity).
        return a > b ? s : best
    })
}

/** Keep one row per session type for a date (latest applicable_from wins on overlap). */
export function collapseSessionsForDate(
    sessions: TradingSession[],
    date: Date | string
): TradingSession[] {
    const byType = new Map<string, TradingSession[]>()
    for (const session of sessions) {
        if (!isTradingSessionApplicableOn(session, date)) continue
        const type = session.type || ''
        const list = byType.get(type) ?? []
        list.push(session)
        byType.set(type, list)
    }
    const out: TradingSession[] = []
    for (const [, versions] of byType) {
        const picked = pickApplicableSession(versions, date)
        if (picked) out.push(picked)
    }
    return out
}

function isDefaultOrUnknownScope(scope: TradingSessionScope | undefined): boolean {
    return (
        scope == null ||
        scope === TradingSessionScope.DEFAULT ||
        scope === TradingSessionScope.TRADING_SESSION_SCOPE_UNKNOWN ||
        scope === TradingSessionScope.UNRECOGNIZED
    )
}

function matchesPreferredScope(
    scope: TradingSessionScope | undefined,
    preferredScope: TradingSessionScope
): boolean {
    return preferredScope === TradingSessionScope.DEFAULT
        ? isDefaultOrUnknownScope(scope)
        : scope === preferredScope
}

function filterBySegment(
    sessions: TradingSession[],
    security: TradingSessionSecurityRef | undefined
): TradingSession[] {
    const segment = security?.segment
    if (!hasSegment(segment)) return sessions

    // Exact segment only — never fall back to another segment (e.g. CM for an FO security).
    const exact = sessions.filter((s) => s.segment === segment)
    if (exact.length) return exact

    // Legacy rows without a segment apply to all segments on that exchange.
    return sessions.filter((s) => !hasSegment(s.segment))
}

/**
 * Pick the session applicable on `date`, preferring `preferredScope` then DEFAULT.
 * Keeps historical charts working when CAS rows only apply from a later applicable_from.
 *
 * Never returns a CAS row when the caller asked for DEFAULT (backend parity) — the previous
 * "any applicable session" fallback could select CAS for non-partOfCAS securities after a
 * DEFAULT row's applicable_to ended.
 */
export function pickApplicableSessionForScope(
    sessions: TradingSession[],
    date: Date | string,
    preferredScope: TradingSessionScope = TradingSessionScope.DEFAULT
): TradingSession | undefined {
    const preferred = pickApplicableSession(
        sessions.filter((s) => matchesPreferredScope(s.scope, preferredScope)),
        date
    )
    if (preferred) return preferred

    // CAS (etc.) may be absent until cutover / not yet applicable — fall back to DEFAULT only.
    if (preferredScope !== TradingSessionScope.DEFAULT) {
        return pickApplicableSession(
            sessions.filter((s) => isDefaultOrUnknownScope(s.scope)),
            date
        )
    }

    return undefined
}

/** One row per session type for a date, with preferred-scope then DEFAULT fallback. */
export function collapseSessionsForDateWithScope(
    sessions: TradingSession[],
    date: Date | string,
    preferredScope: TradingSessionScope = TradingSessionScope.DEFAULT
): TradingSession[] {
    const byType = new Map<string, TradingSession[]>()
    for (const session of sessions) {
        const type = session.type || ''
        const list = byType.get(type) ?? []
        list.push(session)
        byType.set(type, list)
    }
    const out: TradingSession[] = []
    for (const [, versions] of byType) {
        const picked = pickApplicableSessionForScope(versions, date, preferredScope)
        if (picked) out.push(picked)
    }
    return out
}

/**
 * Filter sessions for a security: segment → scope (CAS vs DEFAULT) → applicable_from/to.
 * Pass `asOfDate` to keep only versions applicable that day (and collapse overlaps).
 * Omit `asOfDate` to keep all versioned rows (for calendar lookups that resolve per day later).
 *
 * For calendar template storage (`asOfDate` omitted), keep preferred scope + DEFAULT so
 * historical days can fall back when CAS (etc.) is not yet applicable.
 */
export function filterTradingSessionsForSecurity(
    sessions: TradingSession[] | undefined,
    security: TradingSessionSecurityRef | undefined,
    asOfDate?: Date | string
): TradingSession[] {
    if (!sessions?.length) return []

    const bySegment = filterBySegment(sessions, security)
    const preferredScope = resolveTradingSessionScope(security)

    if (asOfDate != null) {
        return collapseSessionsForDateWithScope(bySegment, asOfDate, preferredScope)
    }

    // Calendar lookup path: retain DEFAULT + preferred scope versions for per-day resolve.
    const retained = bySegment.filter(
        (s) => matchesPreferredScope(s.scope, preferredScope) || isDefaultOrUnknownScope(s.scope)
    )
    return retained.length ? retained : bySegment
}

/** Exchange → segment → scope → applicability selection from SuperChart exchangeTradingSessions. */
export function getTradingSessionsForSecurity(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    security: TradingSessionSecurityRef | undefined,
    asOfDate: Date | string = new Date()
): TradingSession[] {
    if (!exchangeTradingSessions?.length || security?.exchange === undefined) return []
    const exchangeSession = exchangeTradingSessions.find((s) => s.exchange === security.exchange)
    return filterTradingSessionsForSecurity(exchangeSession?.tradingSessions, security, asOfDate)
}

/**
 * Calendar template sessions store times as seconds-from-midnight; SuperChart live sessions use epoch ms.
 * Anchor a template to a concrete local day so callers can compare against Date.now().
 */
export function anchorCalendarSessionTemplateToDay(dayMs: number, template: TradingSession): TradingSession {
    return {
        ...template,
        preOpenStartTime: dayMs + (template.preOpenStartTime || 0) * 1000,
        preOpenEndTime: dayMs + (template.preOpenEndTime || 0) * 1000,
        startTime: dayMs + (template.startTime || 0) * 1000,
        endTime: dayMs + (template.endTime || 0) * 1000,
        postCloseStartTime: template.postCloseStartTime
            ? dayMs + template.postCloseStartTime * 1000
            : 0,
        postCloseEndTime: template.postCloseEndTime
            ? dayMs + template.postCloseEndTime * 1000
            : 0,
        casStartTime: template.casStartTime ? dayMs + template.casStartTime * 1000 : 0,
        casEndTime: template.casEndTime ? dayMs + template.casEndTime * 1000 : 0
    }
}

/**
 * Prefer SuperChart live sessions when they include the security's preferred scope (e.g. CAS).
 * Live cache often only ships DEFAULT CM (ends 15:30); for partOfCAS CM use calendar CAS templates
 * (ends 15:15) when live has no matching scope.
 */
export function resolveLiveTradingSessionsForSecurity(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    calendarData: TradingCalendarData | undefined,
    security: TradingSessionSecurityRef | undefined,
    asOfDate: Date | string = new Date()
): TradingSession[] {
    if (security?.exchange === undefined) return []

    const preferredScope = resolveTradingSessionScope(security)
    const live = getTradingSessionsForSecurity(exchangeTradingSessions, security, asOfDate)
    const liveHasPreferredScope =
        preferredScope === TradingSessionScope.DEFAULT ||
        live.some((s) => s.scope === preferredScope)

    if (live.length && liveHasPreferredScope) return live

    const asOf = typeof asOfDate === 'string' ? new Date(asOfDate) : asOfDate
    const dayMs = toDayStart(asOf.getTime()).getTime()
    const fromCalendar = resolveTradingSessionsFromCalendar(calendarData, asOf, security).map((template) =>
        anchorCalendarSessionTemplateToDay(dayMs, template)
    )

    return fromCalendar.length ? fromCalendar : live
}

/**
 * Narrow TradingCalendarData by segment/scope for a security.
 * Keeps all applicable_from/to versions so callers can resolve per calendar day.
 */
export function filterTradingCalendarDataForSecurity(
    calendarData: TradingCalendarData | undefined,
    security: TradingSessionSecurityRef | undefined
): TradingCalendarData | undefined {
    if (!calendarData) return undefined

    const tradingSessions = filterTradingSessionsForSecurity(calendarData.tradingSessions, security)
    const segment = security?.segment

    let holidays = calendarData.holidays
    let specialTradingDays = calendarData.specialTradingDays
    if (hasSegment(segment)) {
        const filteredHolidays = (calendarData.holidays || []).filter(
            (h) => h.segment === segment || !hasSegment(h.segment)
        )
        if (filteredHolidays.length) holidays = filteredHolidays

        const filteredSpecial = (calendarData.specialTradingDays || []).filter(
            (d) => d.segment === segment || !hasSegment(d.segment)
        )
        if (filteredSpecial.length) specialTradingDays = filteredSpecial
    }

    return {
        ...calendarData,
        tradingSessions,
        holidays,
        specialTradingDays
    }
}

export function toCalendarDayKey(date: Date | string): string {
    if (typeof date === 'string') {
        const trimmed = date.trim()
        if (/^\d{8}$/.test(trimmed)) return trimmed
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed.replace(/-/g, '')
    }
    return toApplicableDateKey(date).replace(/-/g, '')
}

export function resolveTradingSessionsFromCalendar(
    calendarData: TradingCalendarData | undefined,
    date: Date | string,
    security?: TradingSessionSecurityRef
): TradingSession[] {
    if (!calendarData?.tradingSessions?.length) return []

    const filtered = filterTradingSessionsForSecurity(calendarData.tradingSessions, security)
    if (!filtered.length) return []

    const preferredScope = resolveTradingSessionScope(security)
    const dayKey = toCalendarDayKey(date)
    const special = (calendarData.specialTradingDays || []).find((d) => d.date === dayKey)
    const types = special?.sessionTypes?.length ? special.sessionTypes : ['MORNING_SESSION']

    return types
        .map((type) =>
            pickApplicableSessionForScope(
                filtered.filter((s) => s.type === type),
                date,
                preferredScope
            )
        )
        .filter((s): s is TradingSession => !!s)
}

export function getRegularSessionMinutes(session: TradingSession | undefined): number | undefined {
    if (!session) return undefined
    const minutes = Math.max(0, Math.round((session.endTime - session.startTime) / 60))
    return minutes > 0 ? minutes : undefined
}
